/**
 * `positions()` çıktısı — Aşama 1'in TEK türetilmiş görünümü.
 *
 * BU EKRANDA KÂR/ZARAR YOK. Maliyet yöntemi, şirket olayları ve çok para
 * birimli değerleme Aşama 3'e ait. Burada yalnızca "elimde ne var" sorusu
 * yanıtlanıyor: ham miktar, ilk/son işlem tarihi, hareket sayısı.
 */

import { useCallback, useState } from "react";
import { MIKTAR_HANE, formatDecimal, decimalSign } from "../lib/decimal";
import { assetLabel, formatCount, formatIsoDate, todayIso } from "../lib/format";
import { M } from "../dil";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useAsync } from "../repo/useAsync";
import {
  Button,
  DateInput,
  EmptyState,
  ErrorBlock,
  Field,
  LoadingBlock,
  Notice,
  NumberCell,
  Screen,
  Tag,
} from "../ui/primitives";

export function PositionsScreen() {
  const { repo, revision } = useRepositoryContext();
  const [asOf, setAsOf] = useState("");

  const load = useCallback(
    () => (asOf === "" ? repo.positions() : repo.positions(asOf)),
    [repo, asOf],
  );
  const { state, reload } = useAsync(load, [repo, asOf, revision]);

  return (
    <Screen
      title={M.rapor.pozisyonlar.baslik}
      description={M.rapor.pozisyonlar.aciklama}
      toolbar={
        <div className="flex items-end gap-2">
          <Field label={M.rapor.pozisyonlar.tarihItibariyla} htmlFor="pos-asof" className="w-40">
            <DateInput
              id="pos-asof"
              value={asOf}
              max={todayIso()}
              onChange={(e) => setAsOf(e.currentTarget.value)}
            />
          </Field>
          {asOf !== "" && <Button onClick={() => setAsOf("")}>{M.rapor.pozisyonlar.buguneDon}</Button>}
        </div>
      }
    >
      <div className="px-4 pt-3">
        <Notice tone="info" title={M.rapor.pozisyonlar.karZararYok}>
          <p>{M.rapor.pozisyonlar.karZararMetni}</p>
        </Notice>
      </div>

      {state.status === "loading" && state.data === undefined && <LoadingBlock label={M.rapor.pozisyonlar.hesaplaniyor} />}
      {state.status === "error" && <ErrorBlock error={state.error} onRetry={reload} />}

      {state.data !== undefined &&
        (state.data.length === 0 ? (
          <EmptyState
            title={M.rapor.pozisyonlar.bosBaslik}
            description={M.rapor.pozisyonlar.bosAciklama}
          />
        ) : (
          <div className="overflow-x-auto p-4">
            <table className="pf-table pf-card overflow-hidden" data-kart-bolum="liste">
              <caption className="sr-only">{M.rapor.pozisyonlar.tabloBasligi}</caption>
              <thead>
                <tr>
                  <th scope="col">{M.rapor.pozisyonlar.varlik}</th>
                  <th scope="col">{M.rapor.pozisyonlar.baglama}</th>
                  <th scope="col" className="pf-num">
                    {M.rapor.pozisyonlar.hamMiktar}
                  </th>
                  <th scope="col" className="pf-num">
                    {M.rapor.pozisyonlar.paraBirimi}
                  </th>
                  <th scope="col" className="pf-num">
                    {M.rapor.pozisyonlar.ilkIslem}
                  </th>
                  <th scope="col" className="pf-num">
                    {M.rapor.pozisyonlar.sonIslem}
                  </th>
                  <th scope="col" className="pf-num">
                    {M.rapor.pozisyonlar.hareket}
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.data.map((p) => (
                  <tr key={`${p.asset.kind}:${assetLabel(p.asset)}:${p.currency}`}>
                    <td className="font-medium text-ink">{assetLabel(p.asset)}</td>
                    <td>
                      <Tag>{p.asset.kind === "tracked" ? M.rapor.pozisyonlar.izlenen : M.rapor.pozisyonlar.elle}</Tag>
                    </td>
                    <td className="pf-num">
                      <NumberCell sign={decimalSign(p.quantity)}>
                        {formatDecimal(p.quantity, { maxFractionDigits: MIKTAR_HANE })}
                      </NumberCell>
                    </td>
                    <td className="pf-num text-muted">{p.currency}</td>
                    <td className="pf-num text-muted">{formatIsoDate(p.firstTradeDate)}</td>
                    <td className="pf-num text-muted">{formatIsoDate(p.lastTradeDate)}</td>
                    <td className="pf-num text-muted">{formatCount(p.transactionCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </Screen>
  );
}
